<template>
  <form class="dd-effect-form" action="#" @submit.prevent="postEffect()">
    <div class="dd-effect-form__fields">
      <div v-if="showNameInput" class="dd-effect-form__field">
        <name-input
          v-model="name"
          :disabled="$store.getters.effectPostInProgress"
          :maxlength="$store.getters.visuals.maxNameLength"
        />
      </div>
      <div v-if="showTextInput" class="dd-effect-form__field">
        <text-input
          v-model="text"
          :disabled="$store.getters.effectPostInProgress"
          :maxlength="$store.getters.visuals.maxTextLength"
          :placeholder="$t('forms.your_message')"
        />
      </div>
    </div>

    <EffectFreeNotice
      v-if="showIncompleteCryptoPaymentForGenerationSessionNotice"
      tone="warning"
    >
      <p>
        {{ $t('forms.incomplete_payment_for_sticker_creation_found') }}
        <a
          class="orange"
          role="button"
          href="#"
          @click.prevent="returnToCryptoPaymentForGenerationSession"
        >
          {{ $t('forms.you_can_return_to_payment_form') }}
        </a>
      </p>
    </EffectFreeNotice>

    <EffectFreeNotice
      v-else-if="showCompletedPaymentForGenerationSessionNotice"
      tone="accent"
    >
      <p>
        {{ $t('forms.completed_payment_for_sticker_creation_found') }}
        <router-link :to="generationURL" class="orange">
          {{ $store.getters.haveUnspentAttempts ? $t('forms.you_can_create_a_sticker') : $t('forms.you_can_select_one_of_the_stickers') }}
        </router-link>
      </p>
    </EffectFreeNotice>

    <div class="dd-effect-form__actions">
      <template v-if="freeEffectMode">
        <vue-recaptcha
          v-if="!existingFreeEffectGenerationSessionSelected"
          ref="recaptcha"
          class="dd-effect-form__captcha"
          :sitekey="$store.getters.googleRecaptchaSiteKey"
          @verify="captchaVerify"
          @error="captchaError"
        >
          <button class="btn-orange" :disabled="!postingEnabled">
            {{ buttonLabel }}
          </button>
        </vue-recaptcha>
        <EffectFreeNotice
          v-else
          tone="accent"
        >
          <p>
            {{ $t('forms.free_session_for_sticker_creation_found') }}
            <router-link :to="generationURL" class="orange">
              {{ $store.getters.haveUnspentAttempts ? $t('forms.you_can_create_a_sticker') : $t('forms.you_can_select_one_of_the_stickers') }}
            </router-link>
          </p>
        </EffectFreeNotice>
      </template>
      <button
        v-else
        class="btn-orange"
        type="button"
        :disabled="!postingEnabled"
        @click.prevent="postEffect()"
      >
        {{ buttonLabel }}
      </button>
    </div>

    <div v-if="error" class="dd-effect-form__error">{{ error }}</div>
  </form>
</template>

<script>
import TextInput from "@/components/general/TextInput/index";
import NameInput from "@/components/general/NameInput/index";
import { VueRecaptcha } from 'vue-recaptcha';
import { PostFreeEffectForSubscription, PostDonationEffect, PostFreeEffect } from "@/api/api";
import { EffectGenerationSessionStatus }from "@/store/models/EffectGenerationSession";
import EffectFreeNotice from "@/new/components/effects/EffectFreeNotice.vue";

export default {
    name: 'EffectPostingForm',
    components: {
        NameInput,
        TextInput,
        VueRecaptcha,
        EffectFreeNotice,
    },
    data() {
        return {
            name: '',
            text: '',
            error: null,
            nameError: null,
            textError: null,
        };
    },
    computed: {
        freeEffectMode() {
            return this.$store.getters.paymentMode == 'free';
        },
        existingFreeEffectGenerationSessionSelected() {
            return this.$store.getters.effectGenerationSession?.status === EffectGenerationSessionStatus.Free && this.$store.getters.selectedEffectIsPreset;
        },
        buttonLabel() {
          if (!this.$store.getters.paidMode && !this.$store.getters.selectedEffectIsPreset) {
            return this.$t('forms.post_effect')
          } else if (!this.$store.getters.paidMode && this.$store.getters.selectedEffectIsPreset) {
            return this.$t('forms.create_sticker')
          } else if (this.showIncompleteCryptoPaymentForGenerationSessionNotice || this.showCompletedPaymentForGenerationSessionNotice) {
            return this.$t('forms.start_new_payment')
          }
          return this.$t('forms.initiate_payment')
        },
        showIncompleteCryptoPaymentForGenerationSessionNotice() {
            const session = this.$store.getters.effectGenerationSession;
            return !!(
              session &&
              session.status === EffectGenerationSessionStatus.PaymentRequired &&
              session.cryptoPaymentDetails &&
              this.$store.getters.selectedEffectIsPreset
            );
        },
        showCompletedPaymentForGenerationSessionNotice() {
            return this.$store.getters.effectGenerationSession?.status  === EffectGenerationSessionStatus.Paid && this.$store.getters.selectedEffectIsPreset;
        },
        generationURL() {
          return `/generate?session=${this.$store.getters.effectGenerationSession?.uuid}`
        },
        showNameInput() {
            if (this.$store.getters.donationOnlyMode || this.$store.getters.selectedEffectIsPreset){
                return false;
            }
            if (this.$store.getters.effectSelectorEnabled) {
                return this.selectedEffect?.nameAllowed;
            }
            return true;
        },
        showTextInput() {
            if (this.$store.getters.donationOnlyMode || this.$store.getters.selectedEffectIsPreset){
                return false;
            }
            if (this.$store.getters.effectSelectorEnabled) {
                return this.selectedEffect?.textAllowed;
            }
            return true;
        },
        postingEnabled() {
            if (this.$store.getters.effectPostInProgress) {
                return false;
            }
            if (this.$store.getters.effectSelectorEnabled && !this.$store.getters.selectedEffect) {
                return false;
            }
            if (this.$store.getters.paymentMode == 'subscription') {
                if (!this.$store.getters.freeEffectsAvailable && !this.$store.getters.freeGenerationEnabled) {
                    return false;
                }
                return true;
            }
            if (this.$store.getters.paymentMode == 'free') {
                if (this.$store.getters.selectedEffectIsPreset) {
                    return true;
                }
                return !!this.$refs.recaptcha?.response;
            }
            if (this.$store.getters.allowedPaymentMethods.includes(this.$store.getters.paymentMode)) {
                return true;
            }
            if (this.$store.getters.paymentMode in this.$store.getters.allowedCryptocurrencies) {
                return true;
            }
            return false;
        },
        selectedEffect() {
            for (let effect of this.$store.getters.effects) {
                if (effect.id == this.$store.getters.selectedEffect) {
                    return effect;
                }
            }
            return undefined;
        },
    },
    methods: {
        captchaVerify(captchaToken) {
          this.postEffect(captchaToken);
        },
        captchaError() {
          this.error = 'Произошла ошибка проверки капчи. Пожалуйста, обновите страницу и попробуйте ещё раз.';
          this.$refs.recaptcha?.reset();
        },
        returnToCryptoPaymentForGenerationSession() {
            this.$store.dispatch('returnToCryptoPaymentForGenerationSession');
        },
        postEffect(captchaToken) {
            this.error = null;
            this.nameError = null;
            this.textError = null;
            this.$store.dispatch('setEffectPostInProgress', true);
            let future = null;
            let effect = this.$store.getters.effectSelectorEnabled ? this.$store.getters.selectedEffect : null;
            if (this.$store.getters.paymentMode == 'subscription') {
                future = PostFreeEffectForSubscription(effect, this.name, this.text);
            } else if (this.$store.getters.paymentMode == 'donation') {
                future = PostDonationEffect(effect, this.name, this.text, this.$store.getters.total, this.$store.getters.channelCurrency, this.$store.getters.channelCurrency);
            } else if (this.$store.getters.paymentMode in this.$store.getters.allowedCryptocurrencies) {
                future = PostDonationEffect(effect, this.name, this.text, this.$store.getters.total, this.$store.getters.channelCurrency, this.$store.getters.paymentMode);
            } else if (this.freeEffectMode) {
                future = PostFreeEffect(effect, this.name, this.text, captchaToken);
            } else {
                this.$store.dispatch('setEffectPostInProgress', false);
                this.error = 'Не выбран способ оплаты';
            }
            future.then(function(response) {
                if (response.data.ok || (response.data.success && !response.data.generation_session_id)) {
                    this.text = '';
                    this.$router.push('/wall/sent');
                } else if (response.data.error) {
                    this.error = response.data.error;
                } else if (response.data.generation_session_id) {
                  this.$router.push( `/wall/generate?session=${response.data.generation_session_id}`)
                } else if (response.data.redirect) {
                    window.location.href = response.data.redirect;
                    return;
                } else if (response.data.address && response.data.qr_code) {
                    var payment_data = {
                        address: response.data.address,
                        qr_code: response.data.qr_code,
                        address_uuid: response.data.address_uuid,
                        amount: response.data.amount,
                    }
                    this.$store.dispatch('startCryptoPayment', payment_data);
                    if (response.data.effect_generation_session) {
                        this.$store.dispatch('setEffectGenerationSession', response.data.effect_generation_session);
                    }
                }
                if (response.data.limits) {
                    this.$store.dispatch('setLimits', response.data.limits);
                }
                if (response.data.generation_sessions_left !== undefined) {
                    this.$store.dispatch('setFreeGenerationSessionsLeft', response.data.generation_sessions_left);
                }
                if (response.data.seconds_until_next_free_effect) {
                    this.$store.dispatch('setSecondsUntilNextFreeEffect', response.data.seconds_until_next_free_effect);
                }
                this.$refs.recaptcha?.reset();
                this.$store.dispatch('setEffectPostInProgress', false);
            }.bind(this)).catch(function(error) {
                this.$store.dispatch('setEffectPostInProgress', false);
                if (error.response && error.response.status == 400) {
                    let data = error.response.data;
                    this.error = data.error;
                    if (data.fieldError) {
                        this.nameError = data.fieldError.name;
                        this.textError = data.fieldError.text;
                    }
                    return;
                }
                this.error = 'При отправке эффекта возникла проблема. Попробуйте ещё раз.';
                this.$refs.recaptcha?.reset();
            }.bind(this));
        }
    }
};
</script>
